<template>
  <v-sheet>
    <v-navigation-drawer
      v-model="$store.state.rightBarShow"
      width="400"
      absolute
      temporary
      right
    >
      <v-tabs v-model="activeTab" grow dense>
        <v-tab>
          <v-icon left small>mdi-file-multiple-outline</v-icon>
          文件列表
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-image-multiple-outline</v-icon>
          缩略图
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Tab 1: 文件列表（原有功能） -->
        <v-tab-item>
          <v-list flat>
            <v-list-item-group
              v-model="currentFileIdx"
              mandatory
              color="primary"
              @change="switchFile"
            >
              <v-list-item
                v-for="item in files"
                :key="item.title"
              >
                <v-list-item-icon>
                  <v-icon v-text="item.icon"></v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="item.title" :value="item.fid"></v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="deleteFile(item.fid)">
                    <v-icon color="red lighten-1">mdi-delete-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-tab-item>

        <!-- Tab 2: 白板页缩略图 -->
        <v-tab-item>
          <div class="thumbnail-panel">
            <!-- 工具栏 -->
            <div class="thumbnail-toolbar">
              <v-switch
                v-model="autoRefresh"
                label="自动刷新"
                dense
                hide-details
                class="mt-0 mr-2"
                style="flex: 0 0 auto;"
              ></v-switch>
              <v-select
                v-if="autoRefresh"
                v-model="refreshInterval"
                :items="intervalOptions"
                dense
                hide-details
                outlined
                style="max-width: 90px; flex: 0 0 auto;"
                @change="restartAutoRefresh"
              ></v-select>
              <v-spacer></v-spacer>
              <v-btn small color="primary" @click="refreshThumbnails" :loading="refreshing">
                <v-icon left small>mdi-refresh</v-icon>
                刷新
              </v-btn>
            </div>

            <!-- 缩略图列表 -->
            <div class="thumbnail-list" v-if="boardList.length > 0">
              <div
                v-for="(board, index) in boardList"
                :key="board.boardId"
                class="thumbnail-card"
                :class="{ 'thumbnail-card--active': board.boardId === currentBoardId }"
                @click="gotoBoard(board.boardId)"
              >
                <div class="thumbnail-header">
                  <span class="thumbnail-index">{{ index + 1 }}</span>
                  <span class="thumbnail-id" :title="board.boardId">{{ board.boardId }}</span>
                  <v-icon
                    v-if="board.boardId === currentBoardId"
                    small
                    color="primary"
                    class="ml-1"
                  >mdi-eye</v-icon>
                </div>
                <div class="thumbnail-image-wrap">
                  <img
                    v-if="$store.state.boardSnapshots[board.boardId]"
                    :src="$store.state.boardSnapshots[board.boardId]"
                    class="thumbnail-image"
                    alt="缩略图"
                  />
                  <div v-else class="thumbnail-placeholder">
                    <v-icon large color="grey lighten-1">mdi-image-off-outline</v-icon>
                    <span class="grey--text text--lighten-1 caption mt-1">点击"刷新"生成</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="thumbnail-empty">
              <v-icon large color="grey lighten-1">mdi-note-outline</v-icon>
              <span class="grey--text mt-2">暂无白板页</span>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-navigation-drawer>
  </v-sheet>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: 'Rightbar',
  data() {
    return {
      window,
      drawer: false,
      files: [],
      fileList: [],
      currentFile: null,
      currentFileIdx: 0,
      activeTab: 0,
      // 缩略图相关
      boardList: [],
      currentBoardId: '',
      autoRefresh: false,
      refreshInterval: 5,
      refreshing: false,
      refreshTimer: null,
      pendingBoardIds: new Set(),
      refreshTimeoutId: null,
      refreshStartTime: 0,
      snapshotHandler: null,
      intervalOptions: [
        { text: '1秒', value: 1 },
        { text: '2秒', value: 2 },
        { text: '3秒', value: 3 },
        { text: '5秒', value: 5 },
        { text: '10秒', value: 10 },
      ],
    };
  },
  watch: {
    '$store.state.rightBarShow'(value) {
      if (value) {
        this.loadFileList();
        if (this.activeTab === 1) {
          this.loadBoardList();
          this.registerSnapshotListener();
          this.refreshThumbnails();
          // 面板重新打开时，如果自动刷新开关仍为开启状态则重启定时器
          if (this.autoRefresh) {
            this.startAutoRefresh();
          }
        }
      } else {
        this.stopAutoRefresh();
        this.unregisterSnapshotListener();
      }
    },
    activeTab(val) {
      if (val === 1) {
        this.loadBoardList();
        this.registerSnapshotListener();
        this.refreshThumbnails();
        if (this.autoRefresh) {
          this.startAutoRefresh();
        }
      } else {
        this.stopAutoRefresh();
        this.unregisterSnapshotListener();
      }
    },
    autoRefresh(val) {
      if (val && this.activeTab === 1) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    this.unregisterSnapshotListener();
    clearTimeout(this.refreshTimeoutId);
  },
  methods: {
    ...mapActions(['setRightBarShow']),

    loadFileList() {
      this.fileList = window.teduBoard.getFileInfoList();
      this.currentFile = window.teduBoard.getCurrentFile();
      this.files = this.fileList.map(f => ({
        fid: f.fid,
        title: f.title,
        icon: 'mdi-file-outline',
      }));
      this.currentFileIdx = this.files.findIndex(value => value.fid === this.currentFile);
    },

    loadBoardList() {
      if (!window.teduBoard) return;
      const currentFile = window.teduBoard.getCurrentFile();
      const boardIds = window.teduBoard.getFileBoardList(currentFile);
      this.currentBoardId = window.teduBoard.getCurrentBoard();
      this.boardList = boardIds.map(id => ({ boardId: id }));
    },

    refreshThumbnails() {
      if (!window.teduBoard) return;
      // 先刷新白板页列表，确保能获取到最新的 board（如远端切换了页面）
      this.loadBoardList();
      if (this.boardList.length === 0) return;
      this.refreshing = true;
      this.refreshStartTime = Date.now();
      const boardIds = this.boardList.map(b => b.boardId);
      this.pendingBoardIds = new Set(boardIds);

      // 动态计算缩略图实际渲染尺寸，确保 intrinsic size 与 rendered size 匹配
      const dpr = window.devicePixelRatio || 1;
      let thumbWidth = 372; // 默认值
      let thumbHeight = 279;
      const imageEl = this.$el && this.$el.querySelector('.thumbnail-image-wrap');
      if (imageEl) {
        const rect = imageEl.getBoundingClientRect();
        thumbWidth = Math.round(rect.width);
        thumbHeight = Math.round(rect.height);
      }
      // 乘以 devicePixelRatio 确保高清屏下也清晰
      window.teduBoard.snapshotByBoardIds(boardIds, {
        width: Math.round(thumbWidth * dpr),
        height: Math.round(thumbHeight * dpr),
        pixelRatio: 1, // 已经按 dpr 放大了，不再额外超采样
      });

      // 安全超时：防止事件始终未返回时 loading 卡死
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = setTimeout(() => {
        this.refreshing = false;
        this.pendingBoardIds.clear();
      }, 10000);
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimer = setInterval(() => {
        this.loadBoardList();
        this.refreshThumbnails();
      }, this.refreshInterval * 1000);
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    restartAutoRefresh() {
      if (this.autoRefresh) {
        this.startAutoRefresh();
      }
    },

    registerSnapshotListener() {
      if (this.snapshotHandler || !window.teduBoard) return;
      this.snapshotHandler = (data) => {
        this.onSnapshotUpdated(data.boardId);
      };
      window.teduBoard.on(TEduBoard.EVENT.TEB_BOARD_SNAPSHOT_UPDATED, this.snapshotHandler);
    },

    unregisterSnapshotListener() {
      if (this.snapshotHandler && window.teduBoard) {
        window.teduBoard.off(TEduBoard.EVENT.TEB_BOARD_SNAPSHOT_UPDATED, this.snapshotHandler);
        this.snapshotHandler = null;
      }
    },

    onSnapshotUpdated(boardId) {
      this.pendingBoardIds.delete(boardId);
      if (this.pendingBoardIds.size === 0 && this.refreshing) {
        // 保证至少展示 500ms loading 效果
        const elapsed = Date.now() - this.refreshStartTime;
        const remaining = Math.max(0, 500 - elapsed);
        setTimeout(() => {
          this.refreshing = false;
          clearTimeout(this.refreshTimeoutId);
        }, remaining);
      }
    },

    gotoBoard(boardId) {
      if (window.teduBoard) {
        window.teduBoard.gotoBoard(boardId);
        this.currentBoardId = boardId;
      }
    },

    async switchFile() {
      try {
        const fid = await window.teduBoard.switchFile(this.files[this.currentFileIdx].fid);
        console.log(`============ switchFile回调执行，返回参数为切换文件ID为: ${fid} =============`);
        this.setRightBarShow(false);
      } catch (error) {
        console.log(`switchFile执行错误: ${error}`);
      }
    },

    deleteFile(fid) {
      window.teduBoard.deleteFile(fid);
      this.setRightBarShow(false);
    },
  },
};
</script>

<style scoped>
.thumbnail-panel {
  padding: 8px;
}

.thumbnail-toolbar {
  display: flex;
  align-items: center;
  padding: 4px 8px 12px 8px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 8px;
}

.thumbnail-list {
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  padding: 0 4px;
}

.thumbnail-card {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.thumbnail-card:hover {
  border-color: #90caf9;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.thumbnail-card--active {
  border-color: #1976d2;
  box-shadow: 0 2px 12px rgba(25, 118, 210, 0.3);
}

.thumbnail-header {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 12px;
}

.thumbnail-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1976d2;
  color: white;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
  flex-shrink: 0;
}

.thumbnail-card--active .thumbnail-index {
  background: #1976d2;
}

.thumbnail-id {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
  font-family: monospace;
  font-size: 11px;
}

.thumbnail-image-wrap {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.thumbnail-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.thumbnail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}
</style>
